/**
 * LoginPage -- Standalone sign-in page for browser context.
 *
 * Rendered under PlatformLayout at /login. Reuses useLogin hook from
 * @/lib/auth (same mutation AuthCard uses inline). After successful
 * authentication the user is redirected to /account. The Remember Me
 * checkbox defaults to true (same UX as the embedded AuthCard, issue #81).
 *
 * Links:
 *  - "Forgot password?" → /forgot-password
 *  - "Don't have an account? Register" → /register
 */

import { useEffect, useRef, useState } from 'react'
import { Link, useNavigate, useSearchParams } from 'react-router-dom'
import { Mail, Lock, Loader2, AlertCircle } from 'lucide-react'
import { toast } from 'sonner'
import { useLogin, oauthErrorMessage, OAUTH_ERROR_MESSAGE } from '@/lib/auth'
import { Button } from '@/shared/components/forms/Button'
import { Input } from '@/shared/components/forms/Input'
import { OAuthButtons } from './OAuthButtons'

export function LoginPage() {
  const navigate = useNavigate()
  const loginMutation = useLogin()
  const [searchParams] = useSearchParams()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [rememberMe, setRememberMe] = useState(true)

  // Surface OAuth failures the backend redirected here with. The backend
  // 302s failed social sign-ins to /login?oauth_error=<reason>.
  //
  // Two contexts:
  //  • POPUP (window.opener present — the popup-based sign-in landed its
  //    failure here): DON'T toast inside the throwaway popup. Relay the reason
  //    to the opener via postMessage (same-origin targetOrigin, never '*') and
  //    close — the opener (useOAuthSignIn) shows the friendly toast.
  //  • INLINE (no opener — normal page / WebView2 fallback): show a friendly
  //    toast and never leak the raw reason code.
  //
  // Guarded so it fires once even under StrictMode's double-effect.
  const oauthErrorHandled = useRef(false)
  useEffect(() => {
    const reason = searchParams.get('oauth_error')
    if (!reason || oauthErrorHandled.current) return
    oauthErrorHandled.current = true

    if (window.opener && window.opener !== window) {
      window.opener.postMessage(
        { type: OAUTH_ERROR_MESSAGE, reason },
        window.location.origin,
      )
      window.close()
      return
    }

    toast.error(oauthErrorMessage(reason))
  }, [searchParams])

  const isPending = loginMutation.isPending
  const hasError = loginMutation.data && !loginMutation.data.success

  // Where to land after a successful sign-in. Defaults to /account, but a
  // ?next= path lets a deep link survive the login detour — e.g. the Discord
  // link button in a purchase email, or the bot's /link command, both of which
  // send people to /link-discord while they may well be signed out.
  //
  // ONLY same-origin relative paths are honoured. A value must start with a
  // single "/" — "//evil.com" and "https://evil.com" are both rejected, or this
  // becomes an open redirect that phishes GameDevBuddy logins.
  const nextParam = searchParams.get('next')
  const redirectTo =
    nextParam && nextParam.startsWith('/') && !nextParam.startsWith('//')
      ? nextParam
      : '/account'

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!email.trim() || !password) return

    const result = await loginMutation.mutateAsync({
      email: email.trim(),
      password,
      rememberMe,
    })

    if (result.success && result.data?.loggedIn) {
      navigate(redirectTo, { replace: true })
    }
  }

  return (
    <div className="flex min-h-[calc(100vh-10rem)] items-center justify-center p-4">
      <div className="w-full max-w-sm">
        {/* Card */}
        <div className="rounded-xl bg-ue-bg-dark/80 border border-ue-border-medium/60 p-6">
          <h1 className="text-lg font-semibold text-ue-text-primary mb-1">
            Sign in to GameDevBuddy
          </h1>
          <p className="text-xs text-ue-text-secondary mb-5">
            Access your modules, purchases, and settings.
          </p>

          {/* Server error banner */}
          {hasError && (
            <div className="p-2.5 mb-4 bg-ue-accent-red/10 border border-ue-accent-red/20 rounded text-ue-accent-red text-xs flex items-center gap-1.5">
              <AlertCircle className="w-3.5 h-3.5 shrink-0" />
              {loginMutation.data?.error || 'Login failed. Please try again.'}
            </div>
          )}

          <form onSubmit={handleSubmit} className="space-y-3">
            <Input
              type="email"
              label="Email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              disabled={isPending}
              autoComplete="email"
              autoFocus
            />
            <Input
              type="password"
              label="Password"
              placeholder="••••••••"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              disabled={isPending}
              autoComplete="current-password"
            />

            {/* Remember me + forgot password row */}
            <div className="flex items-center justify-between pt-0.5">
              <label className="flex items-center gap-1.5 text-[11px] text-ue-text-tertiary hover:text-ue-text-secondary transition-colors cursor-pointer select-none">
                <input
                  type="checkbox"
                  checked={rememberMe}
                  onChange={(e) => setRememberMe(e.target.checked)}
                  disabled={isPending}
                  className="h-3 w-3 rounded border border-ue-border-medium bg-ue-bg-darkest/80 accent-ue-accent-blue cursor-pointer"
                  aria-label="Remember me"
                />
                <span>Remember me</span>
              </label>
              <Link
                to="/forgot-password"
                className="text-[11px] text-ue-text-tertiary hover:text-ue-accent-primary transition-colors"
              >
                Forgot password?
              </Link>
            </div>

            <Button
              type="submit"
              size="lg"
              loading={isPending}
              disabled={!email.trim() || !password || isPending}
              className="w-full mt-1"
            >
              {isPending ? (
                <>
                  <Loader2 className="w-4 h-4 animate-spin mr-2" />
                  Signing in…
                </>
              ) : (
                'Sign In'
              )}
            </Button>
          </form>

          {/* Social sign-in — renders only for providers the backend has
              configured; the "or" divider only appears when at least one is
              enabled (OAuthButtons returns null otherwise). */}
          <OAuthButtons showDivider />
        </div>

        {/* Register link below card */}
        <p className="mt-4 text-center text-xs text-ue-text-tertiary">
          Don't have an account?{' '}
          <Link
            to="/register"
            className="text-ue-accent-primary hover:text-ue-accent-primary/80 font-medium transition-colors"
          >
            Create one free
          </Link>
        </p>
      </div>
    </div>
  )
}
